// Defibrillator panel: manual / sync cardiovert / pacing.
// MVP focus: manual mode with explicit charge -> safety prompt -> discharge flow.

import { useState } from 'react';
import Drawer from './Drawer';

type Mode = 'manual' | 'sync' | 'pace';

interface Props {
  open: boolean;
  onClose: () => void;
  onChargeDefib: (joules: number) => void;
  onShock: () => void;
  onSyncCardiovert: (joules: number) => void;
  onPaceTranscutaneous: (rate_bpm: number, mA: number) => void;
}

const ENERGY_OPTIONS = [50, 100, 150, 200, 360];

export default function DefibPanel({ open, onClose, onChargeDefib, onShock, onSyncCardiovert, onPaceTranscutaneous }: Props) {
  const [mode, setMode] = useState<Mode>('manual');
  const [energy, setEnergy] = useState(200);
  const [charged, setCharged] = useState(false);
  const [paceRate, setPaceRate] = useState(70);
  const [paceMa, setPaceMa] = useState(40);

  function charge() {
    setCharged(true);
    onChargeDefib(energy);
  }
  function discharge() {
    onShock();
    setCharged(false);
    onClose();
  }
  function syncCardiovert() {
    if (!confirm(`Synchronised cardioversion at ${energy}J? Confirm pads on, patient sedated.`)) return;
    onSyncCardiovert(energy);
    onClose();
  }
  function startPacing() {
    if (!confirm(`Start transcutaneous pacing at ${paceRate} bpm, ${paceMa} mA?`)) return;
    onPaceTranscutaneous(paceRate, paceMa);
    onClose();
  }

  return (
    <Drawer open={open} onClose={onClose} title="Defibrillator" width="md">
      <div className="flex gap-1 mb-4 bg-ink-800 p-1 rounded">
        {(['manual', 'sync', 'pace'] as Mode[]).map((m) => (
          <button
            key={m}
            onClick={() => { setMode(m); setCharged(false); }}
            className={`flex-1 py-2 rounded text-sm font-medium transition-colors ${mode === m ? 'bg-brand-600 text-white' : 'text-mono-white/70 hover:bg-ink-900'}`}
          >
            {m === 'manual' ? 'Manual' : m === 'sync' ? 'Sync cardiovert' : 'Pace'}
          </button>
        ))}
      </div>

      {mode === 'manual' && (
        <div>
          <p className="text-xs text-mono-white/50 mb-3">Manual mode. Adult biphasic: 150–200 J first shock; max output thereafter.</p>
          <div className="grid grid-cols-5 gap-2 mb-4">
            {ENERGY_OPTIONS.map((j) => (
              <button
                key={j}
                onClick={() => { setEnergy(j); setCharged(false); }}
                className={`py-3 rounded font-mono text-lg ${energy === j ? 'bg-brand-600 text-white' : 'bg-ink-800 hover:bg-ink-950 text-mono-white'}`}
              >{j} J</button>
            ))}
          </div>
          {!charged ? (
            <button onClick={charge} className="btn-primary w-full text-lg">Charge {energy} J</button>
          ) : (
            <>
              <div className="panel p-3 bg-alarm-amber/10 border-l-2 border-alarm-amber mb-3 text-sm">
                ⚡ Charged at {energy} J · <span className="text-alarm-amber font-semibold">CALL "STAND CLEAR"</span> · check pads on, no contact with patient, no oxygen flow over chest.
              </div>
              <button onClick={discharge} className="w-full btn bg-alarm-red hover:brightness-110 text-white text-lg">
                Discharge — Shock now
              </button>
              <button onClick={() => setCharged(false)} className="btn-ghost w-full mt-2 text-sm">Cancel charge (disarm)</button>
            </>
          )}
        </div>
      )}

      {mode === 'sync' && (
        <div>
          <p className="text-xs text-mono-white/50 mb-3">Synchronised cardioversion. For unstable, perfusing tachyarrhythmias. Adult biphasic: AF 120-200 J, atrial flutter 70-120 J, monomorphic VT 100 J.</p>
          <div className="grid grid-cols-4 gap-2 mb-4">
            {[70, 100, 120, 200].map((j) => (
              <button
                key={j}
                onClick={() => setEnergy(j)}
                className={`py-3 rounded font-mono text-lg ${energy === j ? 'bg-brand-600 text-white' : 'bg-ink-800 hover:bg-ink-950'}`}
              >{j} J</button>
            ))}
          </div>
          <div className="panel p-3 bg-ink-800/50 mb-3 text-xs text-mono-white/70">
            <strong>Pre-flight:</strong> SYNC mode armed · sedation given · airway protected · monitor showing R-waves clearly · pads applied. The shock will deliver only on the next R-wave.
          </div>
          <button onClick={syncCardiovert} className="btn-primary w-full text-lg">Deliver synchronised shock {energy} J</button>
        </div>
      )}

      {mode === 'pace' && (
        <div>
          <p className="text-xs text-mono-white/50 mb-3">Transcutaneous pacing. For symptomatic bradycardia unresponsive to atropine, or while preparing transvenous pacing.</p>
          <label className="block mb-3">
            <span className="text-xs uppercase tracking-widest text-mono-white/40">Rate</span>
            <div className="flex items-center gap-2 mt-1">
              <input type="range" min={40} max={120} value={paceRate} onChange={(e) => setPaceRate(Number(e.target.value))} className="flex-1" />
              <span className="font-mono text-mono-green w-16 text-right">{paceRate}/min</span>
            </div>
          </label>
          <label className="block mb-3">
            <span className="text-xs uppercase tracking-widest text-mono-white/40">Output (mA)</span>
            <div className="flex items-center gap-2 mt-1">
              <input type="range" min={0} max={200} value={paceMa} onChange={(e) => setPaceMa(Number(e.target.value))} className="flex-1" />
              <span className="font-mono text-mono-green w-16 text-right">{paceMa} mA</span>
            </div>
          </label>
          <p className="text-xs text-mono-white/50 mb-3">Increase mA until electrical AND mechanical capture confirmed (palpable pulse). Painful — sedate where possible.</p>
          <button onClick={startPacing} className="btn-primary w-full text-lg">Start pacing</button>
        </div>
      )}
    </Drawer>
  );
}
